import React, { Fragment, useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { Button, Table } from "react-bootstrap";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../../../firebase";
import RegisterFields from "../RegisterFields";
import accessoriesFormFields from "./accessoriesFormFields";

const CATALOG_ORDER_START = {
  resmed: 100,
  wellell: 500,
  ventmed: 600,
  topson: 700,
  philips: 800,
};

const normalizeBrand = (brand) => String(brand || "").trim().toLowerCase();
const isCatalogOrder = (value) =>
  typeof value === "number" && Number.isFinite(value);

const AccessoriesForm = ({
  accessory,
  onSubmit,
  accessories = [],
  catalogLoading = false,
  canManageCatalogOrder = false,
}) => {
  const [formData, setFormData] = useState({});
  const [fileNames, setFileNames] = useState([]);
  const [lots, setLots] = useState([]);

  const selectedBrandCatalogItems = useMemo(() => {
    const selectedBrand = normalizeBrand(formData.brand);
    if (!selectedBrand) return [];

    return accessories
      .filter(
        (item) =>
          normalizeBrand(item.brand) === selectedBrand &&
          isCatalogOrder(item.catalogOrder)
      )
      .sort((a, b) => {
        if (a.catalogOrder !== b.catalogOrder) {
          return a.catalogOrder - b.catalogOrder;
        }
        return String(a.itemName || "").localeCompare(String(b.itemName || ""));
      });
  }, [accessories, formData.brand]);

  const suggestedCatalogOrder = useMemo(() => {
    const selectedBrand = normalizeBrand(formData.brand);
    if (!selectedBrand || catalogLoading) return null;

    if (selectedBrandCatalogItems.length > 0) {
      return (
        selectedBrandCatalogItems[selectedBrandCatalogItems.length - 1]
          .catalogOrder + 10
      );
    }

    return CATALOG_ORDER_START[selectedBrand] || 10;
  }, [catalogLoading, formData.brand, selectedBrandCatalogItems]);

  useEffect(() => {
    if (accessory && accessory.id) {
      // Convert Firestore data to form-friendly format
      const formattedData = { ...accessory };
      
      // Convert numbers to strings for form inputs
      if (formattedData.stockQuantity !== undefined) {
        formattedData.stockQuantity = String(formattedData.stockQuantity);
      }
      if (formattedData.minimumStock !== undefined) {
        formattedData.minimumStock = String(formattedData.minimumStock);
      }
      if (formattedData.unitPrice !== undefined) {
        formattedData.unitPrice = String(formattedData.unitPrice);
      }
      if (formattedData.catalogOrder !== undefined && formattedData.catalogOrder !== null) {
        formattedData.catalogOrder = String(formattedData.catalogOrder);
      }
      
      // Convert Firestore timestamps to date strings
      if (formattedData.incomingDate && formattedData.incomingDate.toDate) {
        formattedData.incomingDate = formattedData.incomingDate.toDate().toISOString().split('T')[0];
      }
      if (formattedData.expiryDate && formattedData.expiryDate.toDate) {
        formattedData.expiryDate = formattedData.expiryDate.toDate().toISOString().split('T')[0];
      }
      
      console.log("Loading existing accessory data:", formattedData);
      setFormData(formattedData);
    } else {
      // Initialize with default values for new accessory
      const defaultData = {
        category: "",
        status: "Active",
        stockQuantity: "0",
        minimumStock: "5",
        itemCode: "",
        itemName: "",
        brand: "",
        model: "",
        size: "",
        unitPrice: "",
        vendor: "",
        lotNo: "",
        location: "",
        note: "",
      };
      console.log("Initializing new accessory form:", defaultData);
      setFormData(defaultData);
    }
  }, [accessory]);

  useEffect(() => {
    if (!accessory || !accessory.id) {
      setLots([]);
      return undefined;
    }
    let active = true;
    getDocs(query(collection(db, "accessories", accessory.id, "lots"), orderBy("incomingDate", "asc")))
      .then((snapshot) => {
        if (active) setLots(snapshot.docs.map((lotDoc) => ({ id: lotDoc.id, ...lotDoc.data() })));
      })
      .catch((error) => console.error("Error loading accessory lots:", error));
    return () => { active = false; };
  }, [accessory]);

  const handleInputChange = (name) => (e) => {
    const value = e.target.value;
    console.log(`Field ${name} changed to:`, value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate item code based on category and item name
    if (name === 'category' || name === 'itemName') {
      const category = name === 'category' ? value : formData.category;
      const itemName = name === 'itemName' ? value : formData.itemName;
      
      if (category && itemName) {
        const categoryPrefix = category.substring(0, 3).toUpperCase();
        const namePrefix = itemName.substring(0, 3).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        const autoCode = `${categoryPrefix}-${namePrefix}-${timestamp}`;
        
        if (!formData.itemCode || formData.itemCode === "") {
          setFormData(prev => ({
            ...prev,
            itemCode: autoCode
          }));
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log("Form data before submission:", formData);
    
    // Validation
    const requiredFields = ['itemCode', 'category', 'itemName', 'stockQuantity'];
    const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    const openingStock = parseInt(formData.stockQuantity, 10) || 0;
    if ((!accessory || !accessory.id) && openingStock > 0 && (!String(formData.lotNo || "").trim() || !formData.incomingDate)) {
      alert("Lot No. and Incoming Date are required for positive opening stock.");
      return;
    }

    const catalogOrderValue = String(formData.catalogOrder || "").trim();
    if (canManageCatalogOrder && catalogOrderValue) {
      const catalogOrder = Number(catalogOrderValue);
      if (!Number.isInteger(catalogOrder) || catalogOrder <= 0) {
        alert("Catalog order must be a positive whole number.");
        return;
      }
    }

    // Convert numeric fields
    const formValues = { ...formData };
    delete formValues.catalogOrder;
    const submitData = {
      ...formValues,
      stockQuantity: parseInt(formValues.stockQuantity) || 0,
      minimumStock: parseInt(formValues.minimumStock) || 0,
      unitPrice: parseFloat(formValues.unitPrice) || null,
      incomingDate: formValues.incomingDate ? new Date(formValues.incomingDate) : null,
      expiryDate: formValues.expiryDate ? new Date(formValues.expiryDate) : null,
    };

    if (canManageCatalogOrder) {
      submitData.catalogOrder = catalogOrderValue
        ? Number(catalogOrderValue)
        : null;
    }

    console.log("Submitting data:", submitData);
    onSubmit(submitData);
  };

  const renderFields = () => {
    return _.map(
      accessoriesFormFields,
      ({
        label,
        name,
        type,
        options,
        required,
        disabled,
        description,
        min,
        step,
        placeholder,
      }) => {
        if (name === "catalogOrder" && !canManageCatalogOrder) {
          return null;
        }

        let value = formData[name] || "";
        
        // Format date values for input fields
        if (type === "date" && value && value.toDate) {
          value = value.toDate().toISOString().split('T')[0];
        } else if (type === "date" && value instanceof Date) {
          value = value.toISOString().split('T')[0];
        }

        // Debug select fields
        if (type === "select") {
          console.log(`Select field ${name} value:`, value, "from formData:", formData[name]);
        }

        return (
          <Fragment key={name}>
            <RegisterFields
              name={name}
              label={label}
              type={type}
              value={value}
              fileNames={fileNames}
              options={options}
              required={required}
              onChange={handleInputChange(name)}
              disabled={
                accessory && accessory.id && ["stockQuantity", "lotNo", "incomingDate", "expiryDate"].includes(name)
                  ? true
                  : name === "catalogOrder"
                  ? disabled || !formData.brand || catalogLoading
                  : disabled
              }
              description={description}
              min={min}
              step={step}
              placeholder={
                name === "catalogOrder" && suggestedCatalogOrder
                  ? `e.g. ${suggestedCatalogOrder}`
                  : placeholder
              }
            />
            {name === "catalogOrder" && (
              <div className="form-group row m-1">
                <div className="col-lg-4"></div>
                <div className="col-lg-6">
                  {formData.brand && catalogLoading ? (
                    <small className="d-block text-muted">
                      Loading current {formData.brand} catalog positions...
                    </small>
                  ) : formData.brand ? (
                    <>
                      <small className="d-block text-muted mb-1">
                        Suggested next order: {suggestedCatalogOrder}
                      </small>
                      {selectedBrandCatalogItems.length > 0 ? (
                        <div
                          className="border-top pt-2"
                          style={{ maxHeight: "180px", overflowY: "auto" }}
                        >
                          <small className="d-block text-muted mb-1">
                            Current {formData.brand} catalog positions
                          </small>
                          {selectedBrandCatalogItems.map((item) => (
                            <small key={item.id} className="d-block">
                              {item.catalogOrder} - {item.itemName || "Untitled item"}
                            </small>
                          ))}
                        </div>
                      ) : (
                        <small className="d-block text-muted">
                          No catalogued items for this brand yet.
                        </small>
                      )}
                    </>
                  ) : (
                    <small className="d-block text-muted">
                      Select a brand to see catalog positions.
                    </small>
                  )}
                </div>
              </div>
            )}
          </Fragment>
        );
      }
    );
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit}>
        {renderFields()}
        {accessory && accessory.id && (
          <div className="mt-4">
            <h5>Lot balances (read-only)</h5>
            <Table bordered striped size="sm" responsive>
              <thead><tr><th>Lot No.</th><th>Incoming</th><th>Expiry</th><th>Balance</th></tr></thead>
              <tbody>
                {lots.map((lot) => {
                  const formatDate = (value) => value
                    ? (value.toDate ? value.toDate() : new Date(value)).toLocaleDateString("en-CA")
                    : "-";
                  return <tr key={lot.id}><td>{lot.lotNo}</td><td>{formatDate(lot.incomingDate)}</td><td>{formatDate(lot.expiryDate)}</td><td>{lot.stockQuantity || 0}</td></tr>;
                })}
                {lots.length === 0 && <tr><td colSpan="4" className="text-center text-muted">No lot records</td></tr>}
              </tbody>
            </Table>
          </div>
        )}
        <div className="form-group row">
          <div className="col-lg-8 ml-auto">
            <Button type="submit" variant="primary">
              {accessory && accessory.id ? "Update" : "Add"} Accessory
            </Button>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default AccessoriesForm;
